/**
 * BLE GATT UUIDs for Web Bluetooth: build-time defaults from Vite env, with optional
 * per-browser overrides in localStorage (Thermal printer help page).
 */

const LS_SERVICE = 'zeettu.printerBle.serviceUuid';
const LS_WRITE = 'zeettu.printerBle.writeCharUuid';

/**
 * Chromium Web Bluetooth rejects uppercase hex in full 128-bit UUID strings.
 */
export function normalizeBleUuid(raw: string): string {
  let s = raw.trim();
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    s = s.slice(1, -1).trim();
  }
  const fullUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (fullUuid.test(s)) {
    return s.toLowerCase();
  }
  const shortAlias = /^0x[0-9a-f]{1,4}$/i;
  if (shortAlias.test(s)) {
    return `0x${s.slice(2).toLowerCase()}`;
  }
  return s;
}

const FULL_BLE_PRIMARY_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Additional primary service UUIDs merged into Web Bluetooth `optionalServices` (comma, semicolon, or whitespace separated).
 * Use when auto-detect misses a firmware-specific service; values from nRF Connect / vendor doc.
 */
export function getPrinterBleExtraOptionalServicesFromEnv(): string[] {
  const raw = String(import.meta.env?.VITE_PRINTER_BLE_EXTRA_OPTIONAL_SERVICES ?? '').trim();
  if (!raw) return [];
  const tokens = raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const tok of tokens) {
    const n = normalizeBleUuid(tok);
    if (!FULL_BLE_PRIMARY_UUID.test(n)) continue;
    const k = n.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

function readEnvRaw(): { service: string; writeChar: string } {
  const service = String(import.meta.env?.VITE_PRINTER_BLE_SERVICE_UUID ?? '').trim();
  const writeChar = String(import.meta.env?.VITE_PRINTER_BLE_WRITE_CHAR_UUID ?? '').trim();
  return { service, writeChar };
}

function readStoredRaw(): { service: string; writeChar: string } {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
    return { service: '', writeChar: '' };
  }
  try {
    const ls = globalThis.localStorage;
    return {
      service: (ls.getItem(LS_SERVICE) ?? '').trim(),
      writeChar: (ls.getItem(LS_WRITE) ?? '').trim(),
    };
  } catch {
    return { service: '', writeChar: '' };
  }
}

/** Values from `.env` / build (may be empty). */
export function getPrinterBleUuidEnvDefaults(): { service: string; writeChar: string } {
  return readEnvRaw();
}

/** Raw override strings saved on this device (may be empty if not set). */
export function getPrinterBleUuidStoredOverrides(): { service: string; writeChar: string } {
  return readStoredRaw();
}

/**
 * Effective UUIDs for connection: per field, device override wins if non-empty, else env.
 * Returns null if either resolved value is missing.
 */
export function resolvePrinterBleUuids(): { service: string; writeChar: string } | null {
  const env = readEnvRaw();
  const st = readStoredRaw();
  const serviceRaw = st.service || env.service;
  const writeCharRaw = st.writeChar || env.writeChar;
  if (!serviceRaw.trim() || !writeCharRaw.trim()) {
    return null;
  }
  return {
    service: normalizeBleUuid(serviceRaw),
    writeChar: normalizeBleUuid(writeCharRaw),
  };
}

/**
 * Resolution if the current form values were saved: each non-empty trimmed field becomes the
 * device override; empty removes override for that field and uses .env.
 */
export function previewBleResolutionFromForm(serviceField: string, writeField: string): {
  service: string;
  writeChar: string;
} | null {
  const env = readEnvRaw();
  const svcRaw = serviceField.trim() || env.service;
  const wRaw = writeField.trim() || env.writeChar;
  if (!svcRaw.trim() || !wRaw.trim()) return null;
  return {
    service: normalizeBleUuid(svcRaw),
    writeChar: normalizeBleUuid(wRaw),
  };
}

/** Persist overrides on this device. Empty string for a field removes that override (falls back to .env). */
export function savePrinterBleUuidOverrides(p: { service: string; writeChar: string }): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    const ls = globalThis.localStorage;
    const svc = p.service.trim();
    const w = p.writeChar.trim();
    if (svc) ls.setItem(LS_SERVICE, svc);
    else ls.removeItem(LS_SERVICE);
    if (w) ls.setItem(LS_WRITE, w);
    else ls.removeItem(LS_WRITE);
  } catch {
    /* quota / private mode */
  }
}

export function clearPrinterBleUuidOverrides(): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    const ls = globalThis.localStorage;
    ls.removeItem(LS_SERVICE);
    ls.removeItem(LS_WRITE);
  } catch {
    /* ignore */
  }
}

export type BleUuidFieldSource = 'device' | 'env';

export function getPrinterBleUuidFieldSources(): {
  service: BleUuidFieldSource | 'missing';
  writeChar: BleUuidFieldSource | 'missing';
} {
  const env = readEnvRaw();
  const st = readStoredRaw();
  const pick = (stored: string, e: string): BleUuidFieldSource | 'missing' => {
    if (stored.trim()) return 'device';
    if (e.trim()) return 'env';
    return 'missing';
  };
  return {
    service: pick(st.service, env.service),
    writeChar: pick(st.writeChar, env.writeChar),
  };
}
